import React, { useState, useEffect, useRef } from 'react';
import { Check, X, BarChart, ArrowRight, ArrowLeft, Download, Save, Github, Shield, Lock, Server, Users, RefreshCw, Briefcase, Award, Activity, Code, Cpu } from 'lucide-react';
import jsPDF from 'jspdf';
// eslint-disable-next-line no-unused-vars
import html2canvas from 'html2canvas';

// Main application component
export default function CybersecurityDueDiligenceTool() {
  // Add a ref for the summary content
  const summaryRef = useRef(null);
  
  // State for storing assessment data
  const [companyInfo, setCompanyInfo] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    assessmentDate: new Date().toISOString().split('T')[0],
    assessor: '',
    fundingRound: '',
    annualRevenue: '',
    securityBudget: '',
    cisoStatus: '',
    securityTeamSize: '',
    industryVertical: '',
    companySize: '',
    investmentAmount: '',
    founderDetails: '',
    foundingTeamNotes: ''
  });
  
  // State for active category
  const [activeCategory, setActiveCategory] = useState(0);

  // State for questions data
  const [categories, setCategories] = useState(securityCategories);

  // State for welcome modal
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  
  // Handle company info changes
  const handleCompanyInfoChange = (e) => {
    setCompanyInfo({
      ...companyInfo,
      [e.target.name]: e.target.value
    });
  };
  
  // Handle answer changes
  // CISO Marketplace central gate — gate UP FRONT (first answer / report export → requireRun).
  const [csmStarted, setCsmStarted] = useState(false);
  const csmEnsureStarted = async () => {
    if (csmStarted) return true;
    const g = (typeof window !== 'undefined') ? window.csmGate : null;
    const ok = g && g.requireRun ? await g.requireRun() : true; // graceful: no gate → allow
    if (ok) setCsmStarted(true);
    return ok;
  };

  const handleAnswerChange = async (categoryIndex, questionIndex, value) => {
    if (!(await csmEnsureStarted())) return; // gate overlay shown — ignore until entitled
    const updatedCategories = [...categories];
    updatedCategories[categoryIndex].questions[questionIndex].answer = value;
    setCategories(updatedCategories);
  };
  
  // Handle notes changes
  const handleNotesChange = (categoryIndex, questionIndex, value) => {
    const updatedCategories = [...categories];
    updatedCategories[categoryIndex].questions[questionIndex].notes = value;
    setCategories(updatedCategories);
  };
  
  // Handle documentation changes
  const handleDocChange = (categoryIndex, questionIndex, value) => {
    const updatedCategories = [...categories];
    updatedCategories[categoryIndex].questions[questionIndex].documentation = value;
    setCategories(updatedCategories);
  };
  
  // Handle risk rating changes
  const handleRiskRatingChange = (categoryIndex, value) => {
    const updatedCategories = [...categories];
    updatedCategories[categoryIndex].riskRating = value;
    setCategories(updatedCategories);
  };
  
  // Handle concerns changes
  const handleConcernsChange = (categoryIndex, value) => {
    const updatedCategories = [...categories];
    updatedCategories[categoryIndex].keyConcerns = value;
    setCategories(updatedCategories);
  };
  
  // GA event helper
  const trackEvent = (name, params) => {
    if (typeof window.trackEvent === 'function') {
      window.trackEvent(name, params);
    }
  };

  // Navigation functions
  const nextCategory = () => {
    if (activeCategory < categories.length) {
      const next = activeCategory + 1;
      setActiveCategory(next);

      if (activeCategory === 0) {
        trackEvent('assessment_started', {
          company_name: companyInfo.companyName || 'unknown',
          funding_round: companyInfo.fundingRound || 'unknown',
          industry: companyInfo.industryVertical || 'unknown'
        });
      } else if (next === categories.length) {
        trackEvent('assessment_completed', {
          completion_pct: calculateCompletion(),
          overall_risk: calculateOverallRisk()
        });
      } else {
        trackEvent('category_completed', {
          category: categories[activeCategory].name,
          risk_rating: categories[activeCategory].riskRating || 'not_rated',
          category_index: activeCategory
        });
      }
    }
  };
  
  const prevCategory = () => {
    if (activeCategory > 0) {
      setActiveCategory(activeCategory - 1);
    }
  };
  
  // Calculate completion percentage
  const calculateCompletion = () => {
    let answered = 0;
    let total = 0;
    
    categories.forEach(category => {
      category.questions.forEach(question => {
        total++;
        if (question.answer !== null) {
          answered++;
        }
      });
    });
    
    return Math.round((answered / total) * 100);
  };
  
  // Calculate overall risk rating
  const calculateOverallRisk = () => {
    const riskScores = {
      'Low': 1,
      'Medium': 2,
      'High': 3
    };
    
    let totalScore = 0;
    let categoriesWithRatings = 0;
    
    categories.forEach(category => {
      if (category.riskRating) {
        totalScore += riskScores[category.riskRating];
        categoriesWithRatings++;
      }
    });
    
    if (categoriesWithRatings === 0) return 'Not Rated';
    
    const avgScore = totalScore / categoriesWithRatings;
    
    if (avgScore <= 1.5) return 'Low';
    if (avgScore <= 2.5) return 'Medium';
    return 'High';
  };
  
  // Generate dynamic recommendations based on assessment results
  const generateRecommendations = () => {
    const overallRisk = calculateOverallRisk();
    const maturityLevels = calculateMaturityLevel(categories);

    const activeMaturityLevel = maturityLevels.find(level => level.active);
    const maturityLevelIndex = maturityLevels.findIndex(level => level.active);

    // Get high risk categories
    const highRiskCategories = categories.filter(cat => cat.riskRating === 'High' && cat.name !== 'Company Information');

    // Get categories with documented key concerns
    const categoriesWithConcerns = categories.filter(cat => cat.keyConcerns && cat.keyConcerns.trim() !== '');

    // Calculate completion percentage
    const completionPct = calculateCompletion();
    
    // Base recommendations that apply to all assessments
    const baseRecommendations = [
      'Review the complete assessment findings with the company\'s security team and key stakeholders',
      'Create a centralized tracking system for security improvements and remediation efforts'
    ];
    
    // Recommendations based on risk level
    const riskBasedRecommendations = [];
    if (overallRisk === 'High') {
      riskBasedRecommendations.push(
        'Conduct an immediate deep-dive security review with third-party experts before finalizing investment terms',
        'Consider creating specific security-related terms in the investment agreement with post-closing milestones',
        'Develop a 90-day remediation plan for critical security issues identified in this assessment'
      );
    } else if (overallRisk === 'Medium') {
      riskBasedRecommendations.push(
        'Prioritize addressing security gaps in high-risk categories within the first 6 months post-investment',
        'Develop quarterly security review cadence with portfolio company leadership',
        'Consider allocating specific portion of investment for security improvements'
      );
    } else if (overallRisk === 'Low') {
      riskBasedRecommendations.push(
        'Maintain and further strengthen the existing security program',
        'Consider showcasing security posture as a competitive advantage in market positioning',
        'Implement annual security assessment cadence to maintain strong security posture'
      );
    }
    
    // Maturity-based recommendations
    const maturityRecommendations = [];
    const maturityLabel = activeMaturityLevel ? activeMaturityLevel.name : 'Unknown';
    if (maturityLevelIndex <= 1) { // Level 1-2
      maturityRecommendations.push(
        `Current maturity is ${maturityLabel} — establish foundational security controls and a basic governance framework before scaling`,
        'Prioritize hiring security leadership or engaging a virtual CISO',
        'Focus on implementing basic security hygiene practices before scaling operations'
      );
    } else if (maturityLevelIndex === 2) { // Level 3
      maturityRecommendations.push(
        `Current maturity is ${maturityLabel} — formalize and document security processes that may be currently ad-hoc`,
        'Implement regular security awareness training program',
        'Consider obtaining baseline security certifications appropriate for industry and size'
      );
    } else if (maturityLevelIndex >= 3) { // Level 4-5
      maturityRecommendations.push(
        `Current maturity is ${maturityLabel} — focus on automation and optimization of existing security processes`,
        'Consider implementing advanced security monitoring and threat hunting capabilities',
        'Develop metrics to demonstrate security ROI and business value alignment'
      );
    }

    // Concern-based recommendations — surface domains where key concerns were documented
    const concernRecommendations = [];
    if (categoriesWithConcerns.length > 0) {
      const concernNames = categoriesWithConcerns
        .filter(cat => cat.name !== 'Company Information')
        .map(cat => cat.name);
      if (concernNames.length > 0) {
        concernRecommendations.push(
          `Documented concerns require follow-up in: ${concernNames.join(', ')} — review assessor notes before finalizing investment terms`
        );
      }
    }

    // Category-specific recommendations
    const categoryRecommendations = [];
    
    // Add recommendations for high-risk categories
    if (highRiskCategories.length > 0) {
      categoryRecommendations.push(`Prioritize remediation in the following high-risk areas: ${highRiskCategories.map(c => c.name).join(', ')}`);
      
      // Specific recommendations for common high-risk categories
      highRiskCategories.forEach(category => {
        switch(category.name) {
          case 'Security Governance & Management':
            categoryRecommendations.push('Establish or strengthen formal security governance structure and oversight');
            break;
          case 'Data Protection & Privacy':
            categoryRecommendations.push('Implement data classification and protection controls for sensitive information');
            break;
          case 'Application Security':
            categoryRecommendations.push('Integrate security into the software development lifecycle and conduct regular security testing');
            break;
          case 'Infrastructure Security':
            categoryRecommendations.push('Enhance network security controls and implement regular vulnerability management');
            break;
          case 'Access Management':
            categoryRecommendations.push('Implement least privilege access controls and multi-factor authentication');
            break;
          case 'Incident Response & Business Continuity':
            categoryRecommendations.push('Develop and test incident response and disaster recovery plans — use ir.breached.company to benchmark IR maturity');
            break;
          case 'AI & Agentic Security':
            categoryRecommendations.push('Establish an AI security governance framework covering LLM usage, agentic AI, and EU AI Act obligations');
            break;
          case 'Compliance & Certifications':
            categoryRecommendations.push('Prioritize compliance gap remediation and develop a certification roadmap aligned with target markets and funding stage');
            break;
          default:
            break;
        }
      });
    }

    // Policy gap recommendations — recommend cyberpolicy.shop / generatepolicy.com for missing policies
    const policyRecommendations = [];
    const govCategory = categories.find(cat => cat.name === 'Security Governance & Management');
    const dataCategory = categories.find(cat => cat.name === 'Data Protection & Privacy');
    const aiCategory = categories.find(cat => cat.name === 'AI & Agentic Security');
    const appSecCategory = categories.find(cat => cat.name === 'Application Security');

    const missingPolicies = [];

    if (govCategory) {
      const noSecPolicy = govCategory.questions.find(q => q.text.includes('documented security policy') && q.answer === 'no');
      if (noSecPolicy) missingPolicies.push('Information Security Policy');

      const noIRPlan = govCategory.questions.find(q => q.text.includes('incident response plan') && q.answer === 'no');
      if (noIRPlan) missingPolicies.push('Incident Response Plan');
    }

    if (dataCategory) {
      const noPrivacyPolicy = dataCategory.questions.find(q => q.text.includes('privacy policy') && q.answer === 'no');
      if (noPrivacyPolicy) missingPolicies.push('Privacy Policy');

      const noRetention = dataCategory.questions.find(q => q.text.includes('retention') && q.answer === 'no');
      if (noRetention) missingPolicies.push('Data Retention & Deletion Policy');
    }

    if (aiCategory) {
      const noAIPolicy = aiCategory.questions.find(q => q.text.includes('AI security policy') && q.answer === 'no');
      if (noAIPolicy) missingPolicies.push('AI Acceptable Use & Security Policy');

      const noShadowAI = aiCategory.questions.find(q => q.text.includes('shadow AI') && q.answer === 'no');
      if (noShadowAI) missingPolicies.push('Shadow AI / Unsanctioned AI Tools Policy');
    }

    if (appSecCategory) {
      const noSDLC = appSecCategory.questions.find(q => q.text.includes('secure software development lifecycle') && q.answer === 'no');
      if (noSDLC) missingPolicies.push('Secure Development Lifecycle (SDLC) Policy');
    }

    if (missingPolicies.length > 0) {
      policyRecommendations.push(
        `Generate missing security policies (${missingPolicies.slice(0, 3).join(', ')}${missingPolicies.length > 3 ? ` +${missingPolicies.length - 3} more` : ''}) using cyberpolicy.shop or generatepolicy.com`
      );
    }

    // Investment-specific recommendations
    const investmentRecommendations = [];
    const investmentCategory = categories.find(cat => cat.name === 'Investment-Specific Security Considerations');
    if (investmentCategory) {
      const budgetQuestions = investmentCategory.questions.filter(q =>
        (q.text.includes('budget') || q.text.includes('cost')) && q.answer === 'no'
      );
      if (budgetQuestions.length > 0) {
        investmentRecommendations.push('Work with the company to develop a security budget aligned with growth plans');
      }

      const regulatoryQuestions = investmentCategory.questions.filter(q =>
        q.text.includes('regulatory') && q.answer === 'yes'
      );
      if (regulatoryQuestions.length > 0) {
        investmentRecommendations.push('Engage legal counsel to assess regulatory risks and compliance obligations');
      }
    }

    // AI-specific investment risk flag
    const aiCat = categories.find(cat => cat.name === 'AI & Agentic Security');
    if (aiCat) {
      const aiNoAnswers = aiCat.questions.filter(q => q.answer === 'no').length;
      if (aiNoAnswers >= 5) {
        investmentRecommendations.push('Significant AI security gaps identified — assess EU AI Act exposure and require a 90-day AI governance remediation plan as an investment condition');
      }
    }

    // Completion-based recommendations
    const completionRecommendations = [];
    if (completionPct < 60) {
      completionRecommendations.push('Complete the remaining assessment sections to gain a comprehensive view of security posture');
    }

    // Combine all recommendations and limit to a reasonable number
    let allRecommendations = [
      ...baseRecommendations,
      ...riskBasedRecommendations,
      ...maturityRecommendations,
      ...concernRecommendations,
      ...categoryRecommendations.slice(0, 3),
      ...policyRecommendations,
      ...investmentRecommendations,
      ...completionRecommendations
    ];

    // Ensure we don't have too many recommendations (max 12)
    if (allRecommendations.length > 12) {
      allRecommendations = allRecommendations.slice(0, 12);
    }

    return allRecommendations;
  };
  
  // Generate and download PDF report
  const prepareReport = async () => {
    if (!(await csmEnsureStarted())) return; // gate the deliverable too (idempotent — no double-charge)
    if (activeCategory !== categories.length) {
      // If we're not on the summary page, navigate to it
      setActiveCategory(categories.length);
      // Return early and let the user press the button again when on the summary page
      return alert('Navigated to summary view. Please click "Export Report" again to generate the PDF.');
    }
    
    try {
      // Create a timestamp for the filename
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const companyNameForFile = companyInfo.companyName ? 
        companyInfo.companyName.replace(/[^a-z0-9]/gi, '-').toLowerCase() : 
        'company';
      const filename = `${companyNameForFile}-security-assessment-${timestamp}.pdf`;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Add a header
      pdf.setFontSize(20);
      pdf.setTextColor(66, 66, 200);
      pdf.text('VC Cybersecurity Due Diligence Report 2026', 20, 20);
      
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
      
      // Company information section
      pdf.setFontSize(16);
      pdf.setTextColor(66, 66, 200);
      pdf.text('Company Information', 20, 40);
      
      // Add company details
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      let y = 50;
      
      const companyDetails = [
        { label: 'Company Name', value: companyInfo.companyName || 'Not provided' },
        { label: 'Contact', value: companyInfo.contactName || 'Not provided' },
        { label: 'Email', value: companyInfo.contactEmail || 'Not provided' },
        { label: 'Assessment Date', value: companyInfo.assessmentDate || 'Not provided' },
        { label: 'Assessor', value: companyInfo.assessor || 'Not provided' },
        { label: 'Funding Round', value: companyInfo.fundingRound || 'Not provided' },
        { label: 'Investment Amount', value: companyInfo.investmentAmount || 'Not provided' },
        { label: 'Annual Revenue', value: companyInfo.annualRevenue || 'Not provided' },
        { label: 'Security Budget', value: companyInfo.securityBudget || 'Not provided' },
        { label: 'CISO Status', value: companyInfo.cisoStatus || 'Not provided' },
        { label: 'Security Team Size', value: companyInfo.securityTeamSize || 'Not provided' },
        { label: 'Industry', value: companyInfo.industryVertical || 'Not provided' },
        { label: 'Company Size', value: companyInfo.companySize || 'Not provided' }
      ];
      
      companyDetails.forEach(detail => {
        pdf.text(`${detail.label}: ${detail.value}`, 20, y);
        y += 6;
        
        // Add new page if we're near the bottom
        if (y > 270) {
          pdf.addPage();
          y = 20;
        }
      });
      
      // Add founder details if provided
      if (companyInfo.founderDetails) {
        pdf.text(`Founder Details:`, 20, y);
        y += 6;
        const founderText = pdf.splitTextToSize(companyInfo.founderDetails, 170);
        pdf.text(founderText, 20, y);
        y += founderText.length * 5 + 2;
        
        if (y > 270) {
          pdf.addPage();
          y = 20;
        }
      }
      
      // Add founding team notes if provided
      if (companyInfo.foundingTeamNotes) {
        pdf.text(`Founding Team Notes:`, 20, y);
        y += 6;
        const teamText = pdf.splitTextToSize(companyInfo.foundingTeamNotes, 170);
        pdf.text(teamText, 20, y);
        y += teamText.length * 5 + 2;
        
        if (y > 270) {
          pdf.addPage();
          y = 20;
        }
      }
      
      // Add completion percentage
      pdf.text(`Assessment Completion: ${calculateCompletion()}%`, 20, y);
      y += 10;
      
      // Overall risk rating
      pdf.setFontSize(16);
      pdf.setTextColor(66, 66, 200);
      pdf.text('Overall Risk Rating', 20, y);
      y += 8;
      
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`${calculateOverallRisk()}`, 20, y);
      y += 10;
      
      // Risk ratings by category
      pdf.setFontSize(16);
      pdf.setTextColor(66, 66, 200);
      pdf.text('Risk Ratings by Category', 20, y);
      y += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      
      categories.slice(1).forEach(category => {
        pdf.text(`${category.name}: ${category.riskRating || 'Not Rated'}`, 20, y);
        y += 6;
        
        // Add new page if we're near the bottom
        if (y > 270) {
          pdf.addPage();
          y = 20;
        }
      });
      
      // Key concerns
      y += 4;
      pdf.setFontSize(16);
      pdf.setTextColor(66, 66, 200);
      pdf.text('Key Concerns', 20, y);
      y += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      
      const concernsFound = categories.slice(1).filter(cat => cat.keyConcerns && cat.keyConcerns.trim() !== '');
      
      if (concernsFound.length > 0) {
        concernsFound.forEach(category => {
          pdf.text(`${category.name}:`, 20, y);
          y += 6;
          
          // Split long text into lines
          const splitText = pdf.splitTextToSize(category.keyConcerns, 170);
          pdf.text(splitText, 20, y);
          y += splitText.length * 6 + 4;
          
          // Add new page if we're near the bottom
          if (y > 260) {
            pdf.addPage();
            y = 20;
          }
        });
      } else {
        pdf.text('No key concerns have been documented.', 20, y);
        y += 10;
      }
      
      // Detailed assessment by category
      pdf.addPage();
      y = 20;
      pdf.setFontSize(16);
      pdf.setTextColor(66, 66, 200);
      pdf.text('Detailed Assessment', 20, y);
      y += 10;
      
      // Loop through each category and its questions
      categories.slice(1).forEach(category => {
        // Add category header
        pdf.setFontSize(14);
        pdf.setTextColor(66, 66, 200);
        pdf.text(category.name, 20, y);
        y += 6;
        
        pdf.setFontSize(10);
        pdf.setTextColor(80, 80, 80);
        const descriptionText = pdf.splitTextToSize(category.description, 170);
        pdf.text(descriptionText, 20, y);
        y += descriptionText.length * 6 + 4;
        
        // Add questions and answers
        pdf.setFontSize(9);
        pdf.setTextColor(0, 0, 0);
        
        category.questions.forEach(question => {
          // Check if we need a new page
          if (y > 260) {
            pdf.addPage();
            y = 20;
          }
          
          // Question text
          const questionText = pdf.splitTextToSize(`Q: ${question.text}`, 170);
          pdf.text(questionText, 20, y);
          y += questionText.length * 5;
          
          // Answer
          let answerText = 'A: ';
          switch(question.answer) {
            case 'yes':
              answerText += 'Yes';
              break;
            case 'no':
              answerText += 'No';
              break;
            case 'na':
              answerText += 'N/A';
              break;
            default:
              answerText += 'Not answered';
          }
          
          pdf.text(answerText, 20, y);
          y += 5;
          
          // Documentation and notes
          if (question.documentation) {
            const docText = pdf.splitTextToSize(`Documentation: ${question.documentation}`, 170);
            pdf.text(docText, 20, y);
            y += docText.length * 5;
          }
          
          if (question.notes) {
            const notesText = pdf.splitTextToSize(`Notes: ${question.notes}`, 170);
            pdf.text(notesText, 20, y);
            y += notesText.length * 5;
          }
          
          y += 5; // Space between questions
        });
        
        // Add risk rating for the category
        pdf.text(`Risk Rating: ${category.riskRating || 'Not Rated'}`, 20, y);
        y += 15;
        
        // Add a new page before the next category
        if (categories.indexOf(category) < categories.length - 2) {
          pdf.addPage();
          y = 20;
        }
      });
      
      // Maturity level
      pdf.addPage();
      y = 20;
      pdf.setFontSize(16);
      pdf.setTextColor(66, 66, 200);
      pdf.text('Maturity Level Assessment', 20, y);
      y += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      
      const maturityLevels = calculateMaturityLevel(categories);
      const activeLevel = maturityLevels.find(level => level.active);
      
      if (activeLevel) {
        pdf.text(`Current Maturity Level: ${activeLevel.name}`, 20, y);
        y += 6;
        pdf.text(`Description: ${activeLevel.description}`, 20, y);
        y += 10;
      } else {
        pdf.text('Maturity level could not be determined.', 20, y);
        y += 10;
      }
      
      // Next steps
      pdf.setFontSize(16);
      pdf.setTextColor(66, 66, 200);
      pdf.text('Recommended Next Steps', 20, y);
      y += 10;
      
      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      
      const nextSteps = generateRecommendations();
      
      nextSteps.forEach((step, index) => {
        const stepText = pdf.splitTextToSize(`${index + 1}. ${step}`, 170);
        pdf.text(stepText, 20, y);
        y += stepText.length * 6;
        
        // Add space between recommendations
        y += 2;
        
        // Add new page if needed
        if (y > 270 && index < nextSteps.length - 1) {
          pdf.addPage();
          y = 20;
        }
      });
      
      // Footer with page numbers
      const pageCount = pdf.internal.getNumberOfPages();
      
      // Add sponsors to the last page
      pdf.setPage(pageCount);
      let lastPageY = pdf.internal.pageSize.getHeight() - 50; // Position near the bottom, but above the footer
      
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Site Sponsors:', pdf.internal.pageSize.getWidth() / 2, lastPageY, { align: 'center' });
      lastPageY += 6;
      
      const sponsors = [
        'microsec.tools',
        'ir.breached.company',
        'cyberinsurancecalc.com',
        'cyberpolicy.shop',
        'generatepolicy.com',
        'cisomarketplace.services',
        'globalcompliancemap.com',
        'startupsecuritykit.com',
        'zerotrustciso.com',
        'ma.cisomarketplace.services'
      ];
      
      const sponsorsText = sponsors.join(' • ');
      pdf.setFontSize(8);
      pdf.text(sponsorsText, pdf.internal.pageSize.getWidth() / 2, lastPageY, { align: 'center' });
      
      // Add page numbers to all pages
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`CISO Marketplace Diligence • diligence.cisomarketplace.services • Page ${i} of ${pageCount}`, pdf.internal.pageSize.getWidth() / 2, 287, { align: 'center' });
      }
      
      // Save the PDF
      pdf.save(filename);

      trackEvent('pdf_exported', {
        company_name: companyInfo.companyName || 'unknown',
        overall_risk: calculateOverallRisk(),
        completion_pct: calculateCompletion(),
        funding_round: companyInfo.fundingRound || 'unknown'
      });

      alert('PDF report generated and downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('There was an error generating the PDF report. Please try again.');
    }
  };
  
  // Save assessment data
  const saveAssessment = () => {
    // In a real application, this would save to a database
    const assessmentData = {
      companyInfo,
      categories,
      completionPercentage: calculateCompletion(),
      overallRisk: calculateOverallRisk(),
      savedAt: new Date().toISOString()
    };
    
    localStorage.setItem('savedAssessment', JSON.stringify(assessmentData));
    // Save to the member portal's Reports tab (logged-in only; no-op for guests).
    try {
      const g = window.csmGate;
      if (g && g.isLoggedIn && g.isLoggedIn() && g.saveReport) {
        g.saveReport({ title: 'Vendor Due Diligence', summary: 'Due diligence posture — ' + calculateCompletion() + '% complete', data: { companyInfo, overallRisk: calculateOverallRisk(), completion: calculateCompletion() } });
      }
    } catch (e) { /* ignore */ }
    trackEvent('assessment_saved', {
      company_name: companyInfo.companyName || 'unknown',
      completion_pct: calculateCompletion()
    });
    alert('Assessment saved! In a production version, this would be saved to your account.');
  };
  
  // Reset assessment to start a new one
  const resetAssessment = () => {
    // Reset company info
    setCompanyInfo({
      companyName: '',
      contactName: '',
      contactEmail: '',
      assessmentDate: new Date().toISOString().split('T')[0],
      assessor: '',
      fundingRound: '',
      annualRevenue: '',
      securityBudget: '',
      cisoStatus: '',
      securityTeamSize: '',
      industryVertical: '',
      companySize: '',
      investmentAmount: '',
      founderDetails: '',
      foundingTeamNotes: ''
    });
    
    // Reset categories to default state
    setCategories(securityCategories);
    
    // Navigate back to first screen
    setActiveCategory(0);
  };
  
  // Load saved data and show welcome modal on first visit
  useEffect(() => {
    const savedData = localStorage.getItem('savedAssessment');
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        setCompanyInfo(parsedData.companyInfo);
        setCategories(parsedData.categories);
      } catch (e) {
        console.error('Error loading saved data');
      }
    }

    const hasVisited = localStorage.getItem('ciso_diligence_visited');
    if (!hasVisited) {
      setShowWelcomeModal(true);
    }
    // Resume after a guest-pay / sign-in bounce (saved assessment already restored above).
    try {
      const g = window.csmGate;
      if (new URLSearchParams(window.location.search).get('gr_session') && g && g.requireRun) {
        g.requireRun().then((ok) => { if (ok) setCsmStarted(true); });
      }
    } catch (e) { /* ignore */ }
  }, []);

  const dismissWelcomeModal = () => {
    localStorage.setItem('ciso_diligence_visited', 'true');
    setShowWelcomeModal(false);
  };
  
  return (
    <div className="flex flex-col min-h-screen bg-slate-100">

      {/* Welcome Modal — first-time visit only */}
      {showWelcomeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-8 relative">
            <div className="flex items-center mb-4">
              <Shield className="h-8 w-8 text-indigo-600 mr-3 flex-shrink-0" />
              <h2 className="text-xl font-bold text-indigo-700">Welcome to CISO Marketplace Diligence</h2>
            </div>

            <p className="text-gray-700 mb-4">
              This is a <strong>browser-based cybersecurity due diligence tool</strong> built for venture capital investors, M&amp;A teams, and anyone assessing the security posture of a company before — or after — an investment.
            </p>

            <div className="bg-indigo-50 rounded-lg p-4 mb-5 space-y-2">
              <div className="flex items-start">
                <span className="text-indigo-600 font-bold mr-2 mt-0.5">01</span>
                <span className="text-gray-700 text-sm"><strong>Fill in company info</strong> — basic details about the investment target</span>
              </div>
              <div className="flex items-start">
                <span className="text-indigo-600 font-bold mr-2 mt-0.5">02</span>
                <span className="text-gray-700 text-sm"><strong>Answer 120+ questions</strong> across 12 security domains including AI &amp; Agentic Security</span>
              </div>
              <div className="flex items-start">
                <span className="text-indigo-600 font-bold mr-2 mt-0.5">03</span>
                <span className="text-gray-700 text-sm"><strong>Rate risk per domain</strong> — Low, Medium, or High — and note key concerns</span>
              </div>
              <div className="flex items-start">
                <span className="text-indigo-600 font-bold mr-2 mt-0.5">04</span>
                <span className="text-gray-700 text-sm"><strong>Get a summary</strong> — overall risk rating, maturity level (1–5), and tailored recommendations</span>
              </div>
              <div className="flex items-start">
                <span className="text-indigo-600 font-bold mr-2 mt-0.5">05</span>
                <span className="text-gray-700 text-sm"><strong>Export a PDF report</strong> ready for your investment committee or LP reporting</span>
              </div>
            </div>

            <p className="text-xs text-gray-500 mb-6">
              No account needed. All data stays in your browser — nothing is sent to a server.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={dismissWelcomeModal}
                className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-500 text-center"
              >
                Start Assessment
              </button>
              <a
                href="https://cisomarketplace.services/product/ma-cybersecurity%20due%20diligence"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { dismissWelcomeModal(); trackEvent('managed_assessment_clicked', { source: 'welcome_modal' }); }}
                className="flex-1 px-4 py-3 bg-white border-2 border-indigo-600 text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 text-center"
              >
                Get a Managed Assessment
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-indigo-700 text-white p-4 shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Shield className="h-6 w-6" />
            <h1 className="text-xl font-bold">CISO Marketplace Diligence</h1>
          </div>
          <div className="flex space-x-3">
            <button 
              onClick={saveAssessment}
              className="flex items-center px-3 py-1 bg-indigo-600 rounded-md hover:bg-indigo-500 text-sm"
            >
              <Save className="h-4 w-4 mr-1" />
              Save
            </button>
            <button 
              onClick={prepareReport}
              className="flex items-center px-3 py-1 bg-indigo-600 rounded-md hover:bg-indigo-500 text-sm"
            >
              <Download className="h-4 w-4 mr-1" />
              Export Report
            </button>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="flex-grow container mx-auto p-4 md:p-6">
        {/* Progress indicator */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-medium">Assessment Progress</h2>
            <span className="text-sm font-medium">{calculateCompletion()}% Complete</span>
          </div>
          <div className="w-full bg-slate-300 rounded-full h-2.5">
            <div 
              className="bg-indigo-600 h-2.5 rounded-full" 
              style={{ width: `${calculateCompletion()}%` }}
            ></div>
          </div>
        </div>
        
        {/* Homepage with SEO-friendly content */}
        {activeCategory === 0 && (
          <>
            {/* SEO-friendly introduction */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-slate-200 mb-6">
              <h1 className="text-2xl font-bold text-indigo-700 mb-4">VC Cybersecurity Due Diligence Assessment Tool 2026</h1>

              <div className="prose max-w-none mb-6">
                <p className="text-gray-700 mb-4">
                  Welcome to <strong>CISO Marketplace Diligence</strong> — the comprehensive cybersecurity due diligence assessment tool built exclusively for venture capital firms and investors. Evaluate the security posture of potential investments, surface material risks, and generate investment committee-ready reports in under an hour.
                </p>

                <h2 className="text-xl font-semibold text-indigo-600 mt-6 mb-3">Why VC Firms Need Cybersecurity Due Diligence in 2026</h2>
                <p className="text-gray-700 mb-4">
                  The 2026 threat landscape has fundamentally changed the risk calculus for venture-backed companies. AI adoption, agentic systems, and expanding regulatory frameworks — including the EU AI Act, DORA, and NIS2 — have introduced new categories of cyber risk that go far beyond traditional perimeter security. Our platform helps VC firms:
                </p>

                <ul className="list-disc pl-6 mb-4 text-gray-700">
                  <li className="mb-2">Identify security vulnerabilities that could undermine investment returns or trigger regulatory penalties</li>
                  <li className="mb-2">Evaluate AI/LLM security practices — prompt injection, model governance, data exfiltration risks</li>
                  <li className="mb-2">Assess supply chain security posture, including CI/CD pipeline integrity and open source risk</li>
                  <li className="mb-2">Benchmark portfolio companies against current industry security standards</li>
                  <li className="mb-2">Develop strategic remediation roadmaps for pre- and post-investment security programs</li>
                  <li className="mb-2">Protect fund portfolio value from cyber incidents and compliance exposure</li>
                </ul>

                <h2 className="text-xl font-semibold text-indigo-600 mt-6 mb-3">How the VC Security Assessment Works</h2>
                <p className="text-gray-700 mb-4">
                  Designed for venture capital workflows, the assessment covers 11 critical security domains with over 100 questions calibrated to startup and growth-stage companies. Risk ratings and maturity levels are calculated automatically, and a detailed PDF report is ready to share with your investment committee.
                </p>

                <div className="bg-indigo-50 p-4 rounded-md border border-indigo-100 mb-6">
                  <h3 className="text-lg font-medium text-indigo-700 mb-2">Built for Venture Capital in 2026</h3>
                  <ul className="list-disc pl-6 text-gray-700">
                    <li className="mb-1">11 security domains — including AI/LLM security and agentic AI governance</li>
                    <li className="mb-1">100+ questions calibrated to venture-backed companies at every stage</li>
                    <li className="mb-1">Covers EU AI Act, DORA, NIS2, GDPR, CCPA, and SEC cyber disclosure requirements</li>
                    <li className="mb-1">Automated risk scoring and CMMI-style maturity level assessment</li>
                    <li className="mb-1">PDF reports formatted for investment committees and LP reporting</li>
                    <li className="mb-1">No account required — all data stays in your browser</li>
                  </ul>
                </div>

                <p className="text-gray-700 mb-4">
                  <time dateTime="2026-04-09">Last updated: April 9, 2026</time> — Assessment criteria are regularly updated to reflect the current threat landscape and regulatory requirements affecting venture-backed companies.
                </p>
              </div>
            </div>
            
            {/* Company information form */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-slate-200 mb-6">
              <h2 className="text-xl font-bold mb-4">Company Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    name="companyName"
                    value={companyInfo.companyName}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                  <input
                    type="text"
                    name="contactName"
                    value={companyInfo.contactName}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contact Email</label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={companyInfo.contactEmail}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Date</label>
                  <input
                    type="date"
                    name="assessmentDate"
                    value={companyInfo.assessmentDate}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assessor Name</label>
                  <input
                    type="text"
                    name="assessor"
                    value={companyInfo.assessor}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Funding Round</label>
                  <select
                    name="fundingRound"
                    value={companyInfo.fundingRound}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select funding round...</option>
                    <option value="Pre-seed">Pre-seed</option>
                    <option value="Seed">Seed</option>
                    <option value="Angel">Angel</option>
                    <option value="Series A">Series A</option>
                    <option value="Series B">Series B</option>
                    <option value="Series C">Series C</option>
                    <option value="Series D+">Series D+</option>
                    <option value="Growth">Growth</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Investment Amount (USD)</label>
                  <input
                    type="text"
                    name="investmentAmount"
                    value={companyInfo.investmentAmount}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="e.g. $5M"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Annual Revenue (USD)</label>
                  <input
                    type="text"
                    name="annualRevenue"
                    value={companyInfo.annualRevenue}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="e.g. $1M-$5M"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Annual Security Budget (USD)</label>
                  <input
                    type="text"
                    name="securityBudget"
                    value={companyInfo.securityBudget}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="e.g. $100K"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CISO Status</label>
                  <select
                    name="cisoStatus"
                    value={companyInfo.cisoStatus}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select CISO status...</option>
                    <option value="Full-time CISO">Full-time CISO</option>
                    <option value="Virtual/Fractional CISO">Virtual/Fractional CISO</option>
                    <option value="MSSP">MSSP</option>
                    <option value="CTO/CIO handles security">CTO/CIO handles security</option>
                    <option value="No dedicated security leadership">No dedicated security leadership</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Security Team Size</label>
                  <input
                    type="number"
                    name="securityTeamSize"
                    value={companyInfo.securityTeamSize}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry Vertical</label>
                  <select
                    name="industryVertical"
                    value={companyInfo.industryVertical}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select industry...</option>
                    <option value="FinTech">FinTech</option>
                    <option value="HealthTech">HealthTech</option>
                    <option value="EdTech">EdTech</option>
                    <option value="E-commerce">E-commerce</option>
                    <option value="SaaS">SaaS</option>
                    <option value="AI/ML">AI/ML</option>
                    <option value="Cybersecurity">Cybersecurity</option>
                    <option value="IoT">IoT</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Energy">Energy</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Size (employees)</label>
                  <select
                    name="companySize"
                    value={companyInfo.companySize}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select company size...</option>
                    <option value="1-10">1-10</option>
                    <option value="11-50">11-50</option>
                    <option value="51-200">51-200</option>
                    <option value="201-500">201-500</option>
                    <option value="501-1000">501-1000</option>
                    <option value="1000+">1000+</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Founder Details</label>
                  <textarea
                    name="founderDetails"
                    value={companyInfo.founderDetails}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md h-20"
                    placeholder="Founder background, experience, previous exits, etc."
                  ></textarea>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Founding Team Notes</label>
                  <textarea
                    name="foundingTeamNotes"
                    value={companyInfo.foundingTeamNotes}
                    onChange={handleCompanyInfoChange}
                    className="w-full p-2 border border-gray-300 rounded-md h-20"
                    placeholder="Composition of founding team, relevant expertise, security experience, etc."
                  ></textarea>
                </div>
              </div>
              
              <div className="mt-6 flex justify-between">
                <div></div>
                <button
                  onClick={nextCategory}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                >
                  Next: {categories[1].name}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              </div>
            </div>
            
            {/* How It Works */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-slate-200 mb-6">
              <h2 className="text-xl font-bold text-indigo-700 mb-6">How It Works: VC Cybersecurity Due Diligence Step by Step</h2>

              <div className="space-y-6">

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">1</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Enter Company &amp; Investment Details</h3>
                    <p className="text-gray-600 text-sm">Fill in the target company's name, funding round, revenue, security budget, CISO status, industry vertical, and founding team notes. This context shapes the recommendations and appears in the final report. You can also add notes about the founder's background and any prior security experience on the team.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">2</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Work Through 12 Security Domains</h3>
                    <p className="text-gray-600 text-sm mb-2">Each domain covers a critical area of security. For every question, select <strong>Yes</strong>, <strong>No</strong>, or <strong>N/A</strong> — then optionally log what documentation was provided and add your own notes. This creates a timestamped audit trail of your due diligence.</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-xs text-gray-500">
                      {['Security Governance', 'Data Protection & Privacy', 'Application Security', 'Infrastructure Security', 'Access Management', 'Incident Response', 'Third-Party Risk', 'Compliance & Certs', 'Monitoring & Testing', 'Security Culture', 'Investment-Specific', 'AI & Agentic Security'].map((d, i) => (
                        <span key={i} className="bg-gray-100 rounded px-2 py-1">{d}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">3</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Rate Risk &amp; Document Key Concerns Per Domain</h3>
                    <p className="text-gray-600 text-sm">At the bottom of each domain, set a risk rating — <span className="text-green-700 font-medium">Low</span>, <span className="text-yellow-700 font-medium">Medium</span>, or <span className="text-red-700 font-medium">High</span> — based on your findings. Use the key concerns field to capture deal-relevant findings, red flags, or items requiring follow-up before term sheet. These feed directly into the executive summary.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">4</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Review the Assessment Summary</h3>
                    <p className="text-gray-600 text-sm">The summary page auto-calculates an <strong>overall risk rating</strong> averaged across all domains, a <strong>security maturity level</strong> (Level 1 Ad-hoc through Level 5 Optimized) based on the percentage of affirmative answers, and <strong>tailored recommendations</strong> specific to the company's risk profile, maturity stage, and identified gaps — including recommendations for generating missing security policies via <a href="https://cyberpolicy.shop" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">cyberpolicy.shop</a> or <a href="https://generatepolicy.com" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline">generatepolicy.com</a>.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold text-sm">5</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Export a PDF Report for Your Investment Committee</h3>
                    <p className="text-gray-600 text-sm">Click <strong>Export Report</strong> from the summary page to generate a structured PDF covering company details, risk ratings by domain, key concerns, detailed Q&amp;A for every question, maturity level assessment, and recommended next steps. The report is formatted for investment committee review, LP reporting, or inclusion in a deal memo.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gray-400 text-white rounded-full flex items-center justify-center font-bold text-sm">+</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Need a Managed Engagement?</h3>
                    <p className="text-gray-600 text-sm">This tool is ideal for initial screening and lightweight due diligence. For deeper technical assessments, penetration testing, or full M&amp;A cybersecurity due diligence with written deliverables and expert sign-off, visit <a href="https://cisomarketplace.services/product/ma-cybersecurity%20due%20diligence" target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline font-medium">CISO Marketplace's M&amp;A Cybersecurity Due Diligence service</a>.</p>
                  </div>
                </div>

              </div>
            </div>

            {/* Additional SEO content at the bottom */}
            <div className="bg-white p-6 rounded-lg shadow-md border border-slate-200 mb-6">
              <h2 className="text-xl font-bold text-indigo-700 mb-4">VC Cybersecurity Due Diligence in 2026: What's Changed</h2>

              <div className="prose max-w-none">
                <p className="text-gray-700 mb-4">
                  The risk surface for venture-backed companies has expanded dramatically. AI adoption is now universal among startups, supply chain attacks have become the preferred vector for sophisticated threat actors, and a wave of new regulations — the EU AI Act, DORA, and NIS2 — has created material compliance exposure for portfolio companies with EU market exposure. Cyber due diligence is no longer optional for VC investors — it's a fiduciary requirement.
                </p>

                <h3 className="text-lg font-semibold text-indigo-600 mt-5 mb-3">AI Security: The New Due Diligence Frontier</h3>
                <p className="text-gray-700 mb-4">
                  As portfolio companies build with LLMs and deploy agentic AI systems, new attack surfaces have emerged. Prompt injection, model poisoning, data exfiltration via AI assistants, and shadow AI usage by employees are now material risks that must be assessed alongside traditional security controls. Our 2026 assessment includes AI-specific governance and security questions to address this gap.
                </p>

                <h3 className="text-lg font-semibold text-indigo-600 mt-5 mb-3">Regulatory Exposure Directly Impacts Exit Valuations</h3>
                <p className="text-gray-700 mb-4">
                  GDPR enforcement actions, SEC cyber disclosure requirements, EU AI Act obligations, and DORA compliance for FinTech companies create significant financial and reputational risk. A regulatory investigation or non-compliance finding can materially reduce exit valuations or delay M&A transactions. Identifying compliance gaps at the due diligence stage protects both the investment and the exit.
                </p>

                <h3 className="text-lg font-semibold text-indigo-600 mt-5 mb-3">Security Posture as a Competitive Moat</h3>
                <p className="text-gray-700 mb-4">
                  Portfolio companies with strong security programs close enterprise deals faster, face fewer security questionnaire delays, and command premium valuations at exit. SOC 2 Type II, ISO 27001, and AI governance frameworks are increasingly required by enterprise procurement teams. Investing in security early accelerates enterprise revenue growth.
                </p>

                <div className="bg-indigo-50 p-5 rounded-md border border-indigo-100 my-6">
                  <h3 className="text-lg font-medium text-indigo-700 mb-3">Start Your 2026 VC Security Assessment</h3>
                  <p className="text-gray-700 mb-4">
                    Fill out the company information form above to begin the self-service assessment — takes 30–45 minutes and generates a report ready for your investment committee.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={nextCategory}
                      className="flex-1 flex items-center justify-center px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-500"
                    >
                      Start Assessment
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                    <a
                      href="https://cisomarketplace.services/product/ma-cybersecurity%20due%20diligence"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center px-4 py-3 bg-white border-2 border-indigo-600 text-indigo-600 rounded-lg font-medium hover:bg-indigo-50"
                    >
                      Get a Managed Assessment
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        
        {/* Assessment categories */}
        {activeCategory > 0 && activeCategory < categories.length && (
          <div className="bg-white p-6 rounded-lg shadow-md border border-slate-200 mb-6">
            <div className="flex items-center mb-4">
              {getCategoryIcon(categories[activeCategory].name)}
              <h2 className="text-xl font-bold ml-2">{activeCategory}. {categories[activeCategory].name}</h2>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600">{categories[activeCategory].description}</p>
            </div>
            
            {/* Questions */}
            <div className="space-y-6 mb-8">
              {categories[activeCategory].questions.map((question, questionIndex) => (
                <div key={questionIndex} className="bg-indigo-50 p-4 rounded-md border border-indigo-100">
                  <p className="font-medium mb-3">{question.text}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mb-3">
                    <div className="flex items-center space-x-3 md:col-span-2">
                      <button
                        onClick={() => handleAnswerChange(activeCategory, questionIndex, 'yes')}
                        className={`px-3 py-1 rounded-md flex items-center ${
                          question.answer === 'yes' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Yes
                      </button>
                      <button
                        onClick={() => handleAnswerChange(activeCategory, questionIndex, 'no')}
                        className={`px-3 py-1 rounded-md flex items-center ${
                          question.answer === 'no' ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        <X className="h-4 w-4 mr-1" />
                        No
                      </button>
                      <button
                        onClick={() => handleAnswerChange(activeCategory, questionIndex, 'na')}
                        className={`px-3 py-1 rounded-md flex items-center ${
                          question.answer === 'na' ? 'bg-gray-300 text-gray-800 border border-gray-400' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        N/A
                      </button>
                    </div>
                    
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        placeholder="Documentation provided"
                        value={question.documentation || ''}
                        onChange={(e) => handleDocChange(activeCategory, questionIndex, e.target.value)}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        placeholder="Notes"
                        value={question.notes || ''}
                        onChange={(e) => handleNotesChange(activeCategory, questionIndex, e.target.value)}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Risk assessment */}
            <div className="bg-slate-50 p-4 rounded-md border border-slate-200 mb-6">
              <h3 className="font-medium mb-3">Risk Rating</h3>
              <div className="flex space-x-3 mb-4">
                <button
                  onClick={() => handleRiskRatingChange(activeCategory, 'Low')}
                  className={`px-4 py-1 rounded-md ${
                    categories[activeCategory].riskRating === 'Low' ? 'bg-green-100 text-green-800 border border-green-300' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  Low
                </button>
                <button
                  onClick={() => handleRiskRatingChange(activeCategory, 'Medium')}
                  className={`px-4 py-1 rounded-md ${
                    categories[activeCategory].riskRating === 'Medium' ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  Medium
                </button>
                <button
                  onClick={() => handleRiskRatingChange(activeCategory, 'High')}
                  className={`px-4 py-1 rounded-md ${
                    categories[activeCategory].riskRating === 'High' ? 'bg-red-100 text-red-800 border border-red-300' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  High
                </button>
              </div>
              
              <h3 className="font-medium mb-2">Key Concerns</h3>
              <textarea
                value={categories[activeCategory].keyConcerns || ''}
                onChange={(e) => handleConcernsChange(activeCategory, e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md h-20"
                placeholder="List key concerns for this category..."
              ></textarea>
            </div>
            
            <div className="flex justify-between">
              <button
                onClick={prevCategory}
                className="flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Previous
              </button>
              {activeCategory < categories.length - 1 ? (
                <button
                  onClick={nextCategory}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                >
                  Next: {categories[activeCategory + 1].name}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={nextCategory}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                >
                  View Summary
                  <BarChart className="ml-2 h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Summary report */}
        {activeCategory === categories.length && (
          <div className="bg-white p-6 rounded-lg shadow-md border border-slate-200 mb-6" ref={summaryRef}>
            <h2 className="text-xl font-bold mb-4">Assessment Summary</h2>
            
            <div className="mb-6">
              <h3 className="font-medium text-lg mb-2">Company Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 bg-slate-50 p-4 rounded-md border border-slate-200">
                <div>
                  <span className="text-gray-600">Company Name:</span>
                  <span className="ml-2 font-medium">{companyInfo.companyName || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Contact:</span>
                  <span className="ml-2 font-medium">{companyInfo.contactName || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Email:</span>
                  <span className="ml-2 font-medium">{companyInfo.contactEmail || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Assessment Date:</span>
                  <span className="ml-2 font-medium">{companyInfo.assessmentDate || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Assessor:</span>
                  <span className="ml-2 font-medium">{companyInfo.assessor || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Funding Round:</span>
                  <span className="ml-2 font-medium">{companyInfo.fundingRound || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Investment Amount:</span>
                  <span className="ml-2 font-medium">{companyInfo.investmentAmount || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Annual Revenue:</span>
                  <span className="ml-2 font-medium">{companyInfo.annualRevenue || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Security Budget:</span>
                  <span className="ml-2 font-medium">{companyInfo.securityBudget || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">CISO Status:</span>
                  <span className="ml-2 font-medium">{companyInfo.cisoStatus || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Security Team Size:</span>
                  <span className="ml-2 font-medium">{companyInfo.securityTeamSize || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Industry:</span>
                  <span className="ml-2 font-medium">{companyInfo.industryVertical || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Company Size:</span>
                  <span className="ml-2 font-medium">{companyInfo.companySize || 'Not provided'}</span>
                </div>
                <div>
                  <span className="text-gray-600">Completion:</span>
                  <span className="ml-2 font-medium">{calculateCompletion()}%</span>
                </div>
              </div>
            </div>
            
            {companyInfo.founderDetails && (
              <div className="mt-4 bg-slate-50 p-4 rounded-md border border-slate-200">
                <h4 className="font-medium mb-2">Founder Details:</h4>
                <p className="text-gray-700 whitespace-pre-line">{companyInfo.founderDetails}</p>
              </div>
            )}
            
            {companyInfo.foundingTeamNotes && (
              <div className="mt-4 bg-slate-50 p-4 rounded-md border border-slate-200">
                <h4 className="font-medium mb-2">Founding Team Notes:</h4>
                <p className="text-gray-700 whitespace-pre-line">{companyInfo.foundingTeamNotes}</p>
              </div>
            )}
            
            <div className="mb-6">
              <h3 className="font-medium text-lg mb-2">Risk Ratings by Category</h3>
              <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
                <div className="grid grid-cols-1 gap-2">
                  {categories.slice(1).map((category, index) => (
                    <div key={index} className="flex justify-between items-center py-1 border-b border-gray-200 last:border-0">
                      <span>{category.name}</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        category.riskRating === 'Low' ? 'bg-green-100 text-green-800' :
                        category.riskRating === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        category.riskRating === 'High' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {category.riskRating || 'Not Rated'}
                      </span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 pt-3 border-t border-gray-300 flex justify-between items-center">
                  <span className="font-medium">Overall Risk Rating:</span>
                  <span className={`px-4 py-1 rounded-full text-sm font-medium ${
                    calculateOverallRisk() === 'Low' ? 'bg-green-100 text-green-800' :
                    calculateOverallRisk() === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    calculateOverallRisk() === 'High' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {calculateOverallRisk()}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="font-medium text-lg mb-2">Key Concerns Summary</h3>
              <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
                {categories.slice(1).filter(cat => cat.keyConcerns && cat.keyConcerns.trim() !== '').length > 0 ? (
                  categories.slice(1).map((category, index) => (
                    category.keyConcerns && category.keyConcerns.trim() !== '' ? (
                      <div key={index} className="mb-3 last:mb-0">
                        <h4 className="font-medium">{category.name}:</h4>
                        <p className="text-gray-700 mt-1">{category.keyConcerns}</p>
                      </div>
                    ) : null
                  ))
                ) : (
                  <p className="text-gray-500 italic">No key concerns have been documented.</p>
                )}
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="font-medium text-lg mb-2">Maturity Level Assessment</h3>
              <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
                <p className="mb-3">Based on the assessment, the company's security maturity level appears to be:</p>
                
                {calculateMaturityLevel(categories).map((level, index) => (
                  <div key={index} className={`flex items-center py-2 px-3 rounded-md mb-2 ${
                    level.active ? 'bg-indigo-100 border border-indigo-300' : ''
                  }`}>
                    <input 
                      type="radio" 
                      checked={level.active} 
                      readOnly 
                      className="h-4 w-4 text-indigo-600 border-gray-300"
                    />
                    <label className="ml-2 block">
                      <span className="font-medium">{level.name}</span>
                      <span className="text-gray-600 text-sm block">{level.description}</span>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="mb-6">
              <h3 className="font-medium text-lg mb-2">Tailored Recommendations</h3>
              <div className="bg-slate-50 p-4 rounded-md border border-slate-200">
                <ol className="list-decimal list-inside space-y-3 text-gray-700">
                  {generateRecommendations().map((recommendation, index) => (
                    <li key={index} className="leading-relaxed">
                      {recommendation}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
            
            <div className="mt-6 flex justify-between">
              <button
                onClick={() => setActiveCategory(categories.length - 1)}
                className="flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Assessment
              </button>
              <div className="flex space-x-3">
                <button
                  onClick={resetAssessment}
                  className="flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Reassess New Company
                </button>
                <button
                  onClick={saveAssessment}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                >
                  <Save className="mr-2 h-4 w-4" />
                  Save Assessment
                </button>
                <button
                  onClick={prepareReport}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-500"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Export Report
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Sponsors Section */}
      <div className="bg-slate-200 py-6">
        <div className="container mx-auto">
          <p className="text-center text-gray-500 mb-4 text-sm">Site Sponsors</p>
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            <a href="https://microsec.tools/" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-indigo-600 text-sm">microsec.tools</a>
            <a href="https://ir.breached.company/" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-indigo-600 text-sm">ir.breached.company</a>
            <a href="https://cyberinsurancecalc.com/" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-indigo-600 text-sm">cyberinsurancecalc.com</a>
            <a href="https://cyberpolicy.shop/" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-indigo-600 text-sm">cyberpolicy.shop</a>
            <a href="https://generatepolicy.com/" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-indigo-600 text-sm">generatepolicy.com</a>
            <a href="https://cisomarketplace.services/" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-indigo-600 text-sm">cisomarketplace.services</a>
            <a href="https://globalcompliancemap.com/" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-indigo-600 text-sm">globalcompliancemap.com</a>
            <a href="https://startupsecuritykit.com/" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-indigo-600 text-sm">startupsecuritykit.com</a>
            <a href="https://zerotrustciso.com/" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-indigo-600 text-sm">zerotrustciso.com</a>
            <a href="https://ma.cisomarketplace.services/" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-indigo-600 text-sm">ma.cisomarketplace.services</a>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-gray-800 text-white p-4">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-center md:text-left mb-4 md:mb-0">
              <p>&copy; 2026 CISO Marketplace Diligence — VC Cybersecurity Due Diligence Tool</p>
              <p className="text-sm text-gray-400 mt-1">Last updated: April 9, 2026 | <a href="https://cisomarketplace.services" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">cisomarketplace.services</a></p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <a href="/privacy-policy" className="text-gray-300 hover:text-white text-sm">Privacy Policy</a>
              <a href="/terms" className="text-gray-300 hover:text-white text-sm">Terms of Service</a>
              <a href="https://support.cisomarketplace.com/" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white text-sm">Contact Us</a>
              <a href="https://cisomarketplace.services" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white text-sm">CISO Marketplace</a>
              <a href="/ai.txt" className="text-gray-300 hover:text-white text-sm">AI.txt</a>
              <a href="/llms.txt" className="text-gray-300 hover:text-white text-sm">LLMs.txt</a>
            </div>
          </div>
          <div className="mt-4 text-center text-xs text-gray-400">
            <p>Cybersecurity due diligence platform for venture capital firms — updated for the 2026 threat landscape including AI/LLM security, EU AI Act, and supply chain risk.</p>
            <p>Keywords: venture capital cybersecurity due diligence, VC security assessment 2026, AI security due diligence, startup security evaluation, investment cyber risk</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Helper function to get category icon
function getCategoryIcon(categoryName) {
  const iconClasses = "h-6 w-6 text-indigo-600";
  
  switch(categoryName) {
    case 'Security Governance & Management':
      return <Shield className={iconClasses} />;
    case 'Data Protection & Privacy':
      return <Lock className={iconClasses} />;
    case 'Application Security':
      return <Code className={iconClasses} />;
    case 'Infrastructure Security':
      return <Server className={iconClasses} />;
    case 'Access Management':
      return <Users className={iconClasses} />;
    case 'Incident Response & Business Continuity':
      return <RefreshCw className={iconClasses} />;
    case 'Third-Party Risk Management':
      return <Briefcase className={iconClasses} />;
    case 'Compliance & Certifications':
      return <Award className={iconClasses} />;
    case 'Security Monitoring & Testing':
      return <Activity className={iconClasses} />;
    case 'Security Culture & Innovation':
      return <Github className={iconClasses} />;
    case 'AI & Agentic Security':
      return <Cpu className={iconClasses} />;
    default:
      return <Shield className={iconClasses} />;
  }
}

// Calculate maturity level based on assessment
function calculateMaturityLevel(categories) {
  const levels = [
    {
      name: 'Level 1 - Ad-hoc/Minimal',
      description: 'High risk - significant concerns',
      active: false
    },
    {
      name: 'Level 2 - Developing',
      description: 'Medium-high risk - multiple gaps',
      active: false
    },
    {
      name: 'Level 3 - Defined',
      description: 'Medium risk - some processes in place',
      active: false
    },
    {
      name: 'Level 4 - Managed',
      description: 'Medium-low risk - established program',
      active: false
    },
    {
      name: 'Level 5 - Optimized',
      description: 'Low risk - mature security program',
      active: false
    }
  ];
  
  // Count yes answers
  let totalQuestions = 0;
  let yesAnswers = 0;
  
  categories.slice(1).forEach(category => {
    category.questions.forEach(question => {
      if (question.answer !== 'na') {
        totalQuestions++;
        if (question.answer === 'yes') {
          yesAnswers++;
        }
      }
    });
  });
  
  if (totalQuestions === 0) return levels.map((level, i) => i === 0 ? {...level, active: true} : level);
  
  const percentageComplete = (yesAnswers / totalQuestions) * 100;
  
  // Determine level based on percentage
  let levelIndex = 0;
  if (percentageComplete >= 85) levelIndex = 4;
  else if (percentageComplete >= 70) levelIndex = 3;
  else if (percentageComplete >= 50) levelIndex = 2;
  else if (percentageComplete >= 25) levelIndex = 1;
  else levelIndex = 0;
  
  return levels.map((level, i) => i === levelIndex ? {...level, active: true} : level);
}

// Security categories data
const securityCategories = [
  {
    name: 'Company Information',
    description: 'Basic information about the company being assessed',
    questions: [],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'Security Governance & Management',
    description: 'Assessment of the organization\'s security leadership, policies, and overall security program management',
    questions: [
      {
        text: 'Does the company have a dedicated security team or at least one person responsible for security?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a documented security policy that is regularly reviewed and updated?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company conduct regular security awareness training for all employees?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a security incident response plan in place?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company established a security budget?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the security budget increase proportionally with company growth?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a defined security roadmap aligned with business objectives?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company undergone any third-party security assessments in the last 12 months?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is the security team adequately staffed for the company\'s size and risk profile?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'Data Protection & Privacy',
    description: 'Assessment of how the organization protects sensitive data and addresses privacy requirements',
    questions: [
      {
        text: 'Is sensitive data encrypted both at rest and in transit?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company maintain a data inventory that identifies all sensitive data?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are there data retention and deletion policies in place?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is the company compliant with relevant privacy regulations (GDPR, CCPA, etc.)?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are data access controls implemented based on least privilege principles?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company conducted a Data Protection Impact Assessment (DPIA)?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company have a formal privacy policy that is reviewed by legal counsel?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company received any privacy complaints or regulatory inquiries?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is customer data segmented from internal company data?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are privacy and security reviews part of new product development?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'Application Security',
    description: 'Assessment of security practices in the software development lifecycle and application protection',
    questions: [
      {
        text: 'Does the company have a secure software development lifecycle (SDLC)?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are security requirements defined before development begins?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is code regularly reviewed for security vulnerabilities?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are static and dynamic application security testing tools used?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company conducted penetration testing on its applications?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a process for managing and patching vulnerabilities?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company use dependency scanning to identify vulnerable components?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a formal process for evaluating and incorporating security tools in the development pipeline?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are security metrics tracked throughout the development process?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a security champion program within the development team?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are developers provided with secure coding training?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company maintain a Software Bill of Materials (SBOM) for its applications?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is the CI/CD pipeline secured against tampering and supply chain attacks (signed artifacts, hardened runners)?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are cryptographic secrets and API keys managed through a dedicated secrets manager rather than hardcoded or stored in source control?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'Infrastructure Security',
    description: 'Assessment of network, cloud, and systems security controls',
    questions: [
      {
        text: 'Is there a documented network architecture with security controls?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are firewalls and intrusion detection/prevention systems deployed?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is infrastructure regularly scanned for vulnerabilities?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are infrastructure changes managed through a secure change management process?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a patch management process for servers and network devices?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are cloud security configurations regularly reviewed?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are development, testing, and production environments properly separated?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company use cloud security posture management tools?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a process for securely decommissioning infrastructure assets?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are infrastructure security metrics regularly reported to leadership?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'Access Management',
    description: 'Assessment of identity and access management practices',
    questions: [
      {
        text: 'Is multi-factor authentication required for all critical systems?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a formal process for provisioning and deprovisioning user access?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are privileged accounts strictly controlled and monitored?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a password policy that enforces strong passwords?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are access rights regularly reviewed and updated?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is single sign-on (SSO) implemented for company applications?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are user access reviews conducted at least quarterly?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a privileged access management (PAM) solution in place?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are third-party access controls and monitoring in place?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are non-human identities (service accounts, API keys, bots, CI/CD tokens) inventoried and managed with least privilege?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a process for rotating and revoking machine credentials (certificates, tokens, keys) on a defined schedule?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'Incident Response & Business Continuity',
    description: 'Assessment of the organization\'s ability to detect, respond to, and recover from security incidents',
    questions: [
      {
        text: 'Is there an incident response plan that is tested regularly?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company maintain logs of security events and incidents?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a business continuity/disaster recovery plan?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are regular backups performed and tested?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company conducted tabletop exercises for security incidents?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a crisis communication plan for security breaches?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company have cyber insurance with adequate coverage?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a documented data breach response procedure that complies with regulations?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company experienced any significant security incidents in the past 24 months?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are recovery time objectives (RTOs) and recovery point objectives (RPOs) defined and tested?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'Third-Party Risk Management',
    description: 'Assessment of how the organization manages security risks from vendors and partners',
    questions: [
      {
        text: 'Does the company assess the security posture of its vendors and partners?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are security requirements included in contracts with third parties?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there an inventory of all third-party relationships and the data they access?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company conduct regular reviews of third-party security?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are there procedures for responding to third-party security incidents?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is vendor access to company data and systems limited and monitored?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are critical vendors required to maintain certain security certifications (SOC 2, ISO 27001, etc.)?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company have a formal process for evaluating security in M&A activities?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a formal off-boarding process for terminated vendor relationships?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are AI and SaaS tools reviewed for security, data handling, and contractual protections before employee or product adoption?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company maintain an inventory of all AI tools (including employee-used tools like Copilot, ChatGPT) that have access to company or customer data?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'Compliance & Certifications',
    description: 'Assessment of regulatory compliance and security certifications',
    questions: [
      {
        text: 'Has the company achieved any security certifications (ISO 27001, SOC 2, etc.)?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is the company compliant with industry-specific regulations?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are regular compliance assessments conducted?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are there documented policies for handling regulatory violations?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a process for tracking and implementing regulatory changes?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a compliance roadmap aligned with growth plans?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company maintain evidence of compliance for audits?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a designated compliance officer or responsible person?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company identified which regulations apply to its business?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are international data transfer mechanisms (e.g., SCCs, Privacy Shield successor) in place if applicable?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company assessed its obligations under the EU AI Act, including AI system risk classification and conformity requirements?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'If operating in financial services or serving regulated financial institutions, has the company assessed DORA (Digital Operational Resilience Act) compliance requirements?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company comply with SEC cybersecurity disclosure requirements, including material incident reporting within 4 business days?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'Security Monitoring & Testing',
    description: 'Assessment of the organization\'s ability to detect and test for security vulnerabilities',
    questions: [
      {
        text: 'Is continuous security monitoring in place?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are external penetration tests conducted at least annually?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company conducted a red team exercise?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are vulnerability assessments performed regularly?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a bug bounty or vulnerability disclosure program?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are security monitoring tools integrated with incident response processes?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a formal process for addressing findings from security tests?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company track security testing coverage across its environment?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are threat hunting activities performed to identify advanced threats?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'Security Culture & Innovation',
    description: 'Assessment of how security is integrated into the organization\'s culture and innovation processes',
    questions: [
      {
        text: 'Is security considered from the earliest stages of product development?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are developers and product teams trained on secure design principles?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Do executives demonstrate commitment to security?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is security integrated into the company\'s innovation process?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a formal process for evaluating new security technologies?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are security metrics included in executive reporting?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company track and reward security improvements?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is security a factor in performance reviews for all employees?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are employees able to report security concerns without fear of retaliation?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'Investment-Specific Security Considerations',
    description: 'Assessment of security aspects directly relevant to investment decisions and risk',
    questions: [
      {
        text: 'Has the company disclosed all material security incidents to potential investors?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a clear budget allocation for security initiatives post-investment?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has technical debt related to security been properly disclosed and documented?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are there any pending regulatory investigations related to security or privacy?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company have adequate security personnel to support growth plans?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are there any significant security enhancements required for the next stage of growth?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company factored security costs into its financial projections?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are security considerations included in the product roadmap?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company identified security as a potential competitive advantage?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a remediation plan for any high-risk security issues?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  },
  {
    name: 'AI & Agentic Security',
    description: 'Assessment of security controls governing AI/LLM usage, agentic systems, and AI-related regulatory obligations — critical for 2026 investments',
    questions: [
      {
        text: 'Does the company have a documented AI security policy governing the use of LLMs, generative AI, and AI-powered tools?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is there a shadow AI / unsanctioned AI tools policy preventing employees from using unapproved AI tools with company or customer data?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are AI/LLM prompts, completions, and agent actions logged and monitored for security anomalies or data exfiltration?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company assessed and mitigated prompt injection risks in any AI-powered product features?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are AI training datasets and fine-tuning data screened to prevent inclusion of sensitive, regulated, or customer data?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are agentic AI systems (autonomous agents with tool use, web access, or code execution) subject to human oversight controls and least-privilege permissions?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Is AI-generated code reviewed for security vulnerabilities before deployment, either by humans or automated scanning tools?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Has the company classified its AI systems under the EU AI Act risk tiers (minimal, limited, high-risk, unacceptable) and taken appropriate compliance steps?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Are third-party AI APIs (OpenAI, Anthropic, Google, etc.) governed by data processing agreements that meet applicable privacy regulations?',
        answer: null,
        documentation: '',
        notes: ''
      },
      {
        text: 'Does the company have controls to detect and prevent sensitive data exfiltration through AI tools (e.g., employees pasting PII, source code, or confidential data into external AI systems)?',
        answer: null,
        documentation: '',
        notes: ''
      }
    ],
    riskRating: null,
    keyConcerns: ''
  }
]; 